import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { ProductLine } from "../intake/types";
import type { ProductFieldPayload } from "../intake/product-fields";
import { dwellingAssetAttrs, productCaptureMeta } from "../intake/product-fields";

// MÜŞTERİ → TEKLİF FORMU · Step 1 capture — ONE call, ONE transaction.
//
// A3, SECOND CHAIN. This ran the same six sequential browser POSTs the phone door
// used to (party → household → member → asset → lead → interaction), each
// committing on its own. Batch 4 converted the phone door and closed A3; this
// file was in the same plan line and was missed, so the orphan-party failure the
// commit message described as eliminated stayed live on the Teklif surface. It is
// now the same POST /v1/intake/leads.
//
// WHAT THE RESUMABILITY WAS FOR. The old hook kept every obtained id in a progress
// ref so a failure mid-chain could be retried without re-creating the party (which
// would trip the CSA-1009 dedup). That machinery existed to survive partial
// commits. With one transaction there are no partial commits to survive: a failure
// leaves nothing behind, so "retry" is just "submit again". The progress ref, the
// per-step guards and the assetError flag are all deleted rather than ported —
// carrying them forward would keep the shape of a problem that no longer exists.
//
// TWO THINGS THE ENDPOINT LEARNED for this door, both because hardcoding them
// would have written a false fact into the KVKK record:
//   • `partyId` — the kayıtlı-üye path attaches to a party already on file
//     instead of creating one. The server re-reads it under the caller's scope.
//   • `channel` — this customer arrived by phone/e-posta/WhatsApp/yüz yüze; the
//     interaction says which, instead of claiming a phone call.

const PLATE_RE = /^[0-9]{2}\s?[A-Z]{1,3}\s?[0-9]{2,4}$/;
const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;
const UAVT_RE = /^\d{10}$/;
const VEHICLE_LINES = new Set<ProductLine>(["trafik", "kasko"]);

/** The wizard's supported lines — the full product-first set (mirrors phone-intake).
 *  Trafik/Kasko → VEHICLE asset; DASK → DWELLING asset; Seyahat/Sağlık are lead-only. */
export type QuoteLine = ProductLine;

/** The interaction channel — the EXACT recordInteractionSchema.channel enum
 *  (packages/validation capture.ts). The UI offers richer "nereden geldi" options
 *  (İnternet reklamı/Instagram/Diğer) which the surface maps onto the nearest enum
 *  value; the human label rides in `channelDetail` (see below). */
export type ContactChannel = "PHONE" | "EMAIL" | "SMS" | "WHATSAPP" | "IN_PERSON" | "PORTAL";

export interface QuoteCaptureInput extends ProductFieldPayload {
  line: QuoteLine;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  /** Required for a NEW party; omitted on the registered-member path (party reused). */
  tckn?: string;
  /** "Nereden geldi?" — the mapped interaction channel enum, recorded on the interaction. */
  channel: ContactChannel;
  /** The advisor's granular "nereden geldi" selection (+ any manual "Diğer" label) that the
   *  strict interaction enum cannot hold — preserved in the ONBOARDING intent's subjectRef
   *  (the only free-form slot on the contract). Settled by design: BE keeps acquisition-source
   *  (this label) distinct from the comms-medium enum, so the source rides here, not the enum. */
  channelDetail?: Record<string, string>;
  /** Advisor attests the aydınlatma was read + service consent captured (ONBOARDING). */
  serviceConsent: boolean;
  /** Optional marketing consent — logged as CROSS_SELL (İYS deferred: no send). */
  marketingConsent: boolean;
  /** Sağlık line only — the SEPARATE health açık rıza the advisor attests. Rides the
   *  ONBOARDING intent's subjectRef (the console cannot write the special-category consent
   *  row directly; parity with the web door's captureHealthConsent is a flagged BE gap). */
  healthConsent?: boolean;
  /** Registered-member path (Kayıtlı üye): attach to an EXISTING party/household instead
   *  of creating them — reuses ids, never POSTs /v1/parties again (no CSA-1009 dedup). */
  existingPartyId?: string;
  existingHouseholdId?: string;
}

export interface QuoteCaptureResult {
  leadId: string;
  partyId: string;
  householdId: string;
  /** REMOVED with the single transaction: `assetId` / `assetError` described the
   *  old chain, where the asset could fail while the lead survived. There is no
   *  partial success left to report — the asset lands with everything else. */
  assetId?: never;
  assetError?: never;
}

/**
 * Map what the advisor typed onto the server's staff-intake boundary shape.
 *
 * Exported so the test can assert the BODY, not just that one request happened —
 * the whole point of the conversion is that this single payload carries what six
 * calls used to, so the payload is the contract worth pinning.
 */
export function toQuoteIntakeBody(input: QuoteCaptureInput): Record<string, unknown> {
  const t = (v: string | undefined): string | undefined => {
    const x = v?.trim();
    return x && x.length > 0 ? x : undefined;
  };

  const plate = input.plate?.trim().toUpperCase();
  const vin = input.vin?.trim().toUpperCase();
  const validPlate = plate && PLATE_RE.test(plate) ? plate : undefined;
  const validVin = vin && VIN_RE.test(vin) ? vin : undefined;
  const label =
    t(
      [input.make, input.model]
        .map((v) => v?.trim())
        .filter(Boolean)
        .join(" "),
    ) ?? undefined;
  const uavt = t(input.uavtCode);
  const validUavt = uavt && UAVT_RE.test(uavt) ? uavt : undefined;

  // An invalid plate/VIN/UAVT is OMITTED rather than sent: the boundary would
  // reject the whole intake, and losing the lead because a plate was mistyped is
  // a worse outcome than capturing the lead without it. Same rule as the phone door.
  const vehicle =
    VEHICLE_LINES.has(input.line) && (validPlate || validVin)
      ? {
          ...(validPlate ? { plate: validPlate } : {}),
          ...(validVin ? { vin: validVin } : {}),
          ...(label ? { label } : {}),
        }
      : undefined;
  const dwelling =
    input.line === "dask" && validUavt
      ? {
          uavtCode: validUavt,
          ...(t(input.address) ? { address: t(input.address) } : {}),
          ...dwellingAssetAttrs(input),
        }
      : undefined;

  // The §4/§5 capture metadata and the granular "nereden geldi" label both land
  // in captureNotes — which the server writes to the interaction's subjectRef,
  // the same destination the old chain used. One free-form slot, unchanged.
  const captureMeta = productCaptureMeta(input.line, input, { healthConsent: input.healthConsent });
  const notes: Record<string, string> = {};
  for (const [k, v] of Object.entries({ ...(input.channelDetail ?? {}), ...(captureMeta ?? {}) })) {
    if (typeof v === "string" && v.trim()) notes[k] = v.trim();
  }

  return {
    product: input.line,
    // EXACTLY ONE identity source — the server rejects both (400).
    ...(input.existingPartyId
      ? { partyId: input.existingPartyId }
      : {
          person: {
            firstName: input.firstName.trim(),
            lastName: input.lastName.trim(),
            phone: input.phone.trim(),
            ...(t(input.email) ? { email: t(input.email) } : {}),
            tckn: (input.tckn ?? "").trim(),
          },
        }),
    channel: input.channel,
    consents: {
      service: true as const,
      ...(input.marketingConsent ? { marketing: true as const } : {}),
    },
    ...(vehicle ? { vehicle } : {}),
    ...(dwelling ? { dwelling } : {}),
    ...(input.line === "seyahat" ? {} : {}),
    ...(Object.keys(notes).length > 0 ? { captureNotes: notes } : {}),
  };
}

/** ONE POST. See the header for what the old resumable chain was compensating for. */
export function useQuoteCapture() {
  const api = useApi();
  const qc = useQueryClient();
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<QuoteCaptureResult | null>(null);

  const run = useCallback(
    async (input: QuoteCaptureInput): Promise<QuoteCaptureResult> => {
      setPending(true);
      setError(null);
      try {
        const res = await api.post<QuoteCaptureResult>(
          "/v1/intake/leads",
          toQuoteIntakeBody(input),
        );
        setResult(res);
        void qc.invalidateQueries({ queryKey: ["leads"] });
        return res;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setPending(false);
      }
    },
    [api, qc],
  );

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
    setPending(false);
  }, []);

  return { run, isPending, error, result, reset };
}
